/**
 * Compound interest — pure calculation logic.
 *
 * Formula (monthly compounding, monthly contributions):
 *   i = r / 12                      (monthly rate)
 *   n = years × 12                  (number of periods)
 *   FV(initial)      = P × (1 + i)^n
 *   FV(contributions) = PMT × ((1 + i)^n − 1) / i × (1 + i)^k
 *      k = 0 when contributions are made at the END of each month (ordinary annuity)
 *      k = 1 when contributions are made at the BEGINNING of each month (annuity-due)
 *   FV = FV(initial) + FV(contributions)
 *
 * Test vector: 10 000 € + 500 €/month at the end of the month, 7% p.a.,
 * 20 years, monthly compounding → ≈ 300 851 €.
 */

export const COMPOUND_VERIFIED_AT = "2026-08-15";

export type ContributionTiming = "end" | "begin";

export interface CompoundInput {
  initialAmount: number;
  monthlyContribution: number;
  /** Nominal annual rate in percent, e.g. 7 for 7%. */
  annualReturnPct: number;
  years: number;
  /** Whether contributions land at the beginning or the end of each month. */
  timing?: ContributionTiming;
}

export interface CompoundResult {
  finalAmount: number;
  totalInvested: number;
  totalProfit: number;
}

export function compoundFutureValue({
  initialAmount,
  monthlyContribution,
  annualReturnPct,
  years,
  timing = "end",
}: CompoundInput): CompoundResult {
  const i = annualReturnPct / 100 / 12;
  const n = Math.round(years * 12);

  const fvInitial = initialAmount * Math.pow(1 + i, n);

  let fvContrib: number;
  if (i === 0) {
    fvContrib = monthlyContribution * n;
  } else {
    fvContrib = monthlyContribution * ((Math.pow(1 + i, n) - 1) / i);
    if (timing === "begin") fvContrib *= 1 + i;
  }

  const finalAmount = fvInitial + fvContrib;
  const totalInvested = initialAmount + monthlyContribution * n;

  return {
    finalAmount,
    totalInvested,
    totalProfit: finalAmount - totalInvested,
  };
}

/** Year-by-year series used by the chart (same formula, evaluated per year). */
export function compoundSeries(input: CompoundInput) {
  const data: { year: number; invested: number; total: number; profit: number }[] = [];
  for (let year = 0; year <= input.years; year++) {
    const { finalAmount, totalInvested } = compoundFutureValue({ ...input, years: year });
    data.push({
      year,
      invested: Math.round(totalInvested),
      total: Math.round(finalAmount),
      profit: Math.round(finalAmount - totalInvested),
    });
  }
  return data;
}
